/**
 * Assets: UpCircle article
 * -----------------------------------------------------------------------------
 * Script for the main-article-upcircle section.
 * Closes the contents box on mobile, keeps sticky parts below the store's
 * header, shows the mobile sticky add to cart bar between the Shop this
 * article card and the end of the post, and hands add to cart forms to the
 * theme's slider cart.
 *
 */
class UpcBlogArticle extends HTMLElement {

  /**
   * Sets up the article once it is on the page.
   */
  connectedCallback() {
    this.mobileQuery = window.matchMedia('(max-width: 700px)')
    this.closeContentsOnMobile()
    this.watchHeader()
    this.addEventListener('submit', (event) => this.onSubmit(event))

    this.stickyBar = this.querySelector('[js-blog-article="stickyBar"]')
    this.shopCard = this.querySelector('[js-blog-card="feature"]')

    this.endSection =
      this.querySelector('[js-blog-article="endProducts"]') ||
      this.querySelector('[js-blog-article="relatedPosts"]')

    if (!this.stickyBar || !this.shopCard || !this.endSection) {
      return
    }

    this.updateStickyBar = this.updateStickyBar.bind(this)
    window.addEventListener('scroll', this.updateStickyBar, { passive: true })
    window.addEventListener('resize', this.updateStickyBar)
    this.updateStickyBar()
  }

  /**
   * Removes listeners and the body state class when the article goes.
   */
  disconnectedCallback() {
    if (this.updateStickyBar) {
      window.removeEventListener('scroll', this.updateStickyBar)
      window.removeEventListener('resize', this.updateStickyBar)
    }

    if (this.headerObserver) {
      this.headerObserver.disconnect()
    }

    if (this.setHeaderOffset) {
      window.removeEventListener('resize', this.setHeaderOffset)
    }

    document.body.classList.remove('upc-blog-sticky-shown')
  }

  /**
   * Starts the contents boxes in the article closed on mobile, so the post
   * starts higher up the screen. The sidebar copy only shows on desktop.
   */
  closeContentsOnMobile() {
    if (!this.mobileQuery.matches) {
      return
    }

    const contentsBoxes = this.querySelectorAll(
      '[js-blog-contents]:not([js-blog-contents="sidebar"])',
    )

    contentsBoxes.forEach((contents) => contents.removeAttribute('open'))
  }

  /**
   * The store's announcement bar and header stay stuck to the top of the
   * screen, so the sidebar card and contents links sit below their bottom
   * edge. Saves that edge as the --upc-header-offset CSS variable.
   */
  watchHeader() {
    const headers = document.querySelectorAll([
      '#shopify-section-header-search',
      '#shopify-section-header-new',
      '#shopify-section-header',
    ].join(', '))

    const header = [...headers].find((element) => element.offsetHeight > 0)

    if (!header) {
      return
    }

    this.setHeaderOffset = () => {
      const style = getComputedStyle(header)
      const isStuck = style.position === 'sticky' || style.position === 'fixed'

      const offset = isStuck
        ? (parseFloat(style.top) || 0) + header.offsetHeight
        : 0

      this.style.setProperty('--upc-header-offset', `${offset}px`)
    }

    this.setHeaderOffset()
    window.addEventListener('resize', this.setHeaderOffset)

    if ('ResizeObserver' in window) {
      this.headerObserver = new ResizeObserver(this.setHeaderOffset)
      this.headerObserver.observe(header)
    }
  }

  /**
   * Shows the mobile sticky bar once the Shop this article card has
   * scrolled off the top, until the end products or related posts come
   * into view.
   */
  updateStickyBar() {
    const cardBounds = this.shopCard.getBoundingClientRect()
    const endBounds = this.endSection.getBoundingClientRect()

    const isShown =
      this.mobileQuery.matches &&
      cardBounds.bottom < 0 &&
      endBounds.top > window.innerHeight - 110

    this.stickyBar.classList.toggle('upc-blog-sticky--shown', isShown)
    this.stickyBar.inert = !isShown
    document.body.classList.toggle('upc-blog-sticky-shown', isShown)
  }

  /**
   * Hands the add to the theme's add_v_to_cart
   * (snippets/preloading_functions.liquid), which adds the item and opens
   * the store's slider cart. If it is missing the form posts to /cart/add.
   * @param {SubmitEvent} event - Submit event from a form in the article.
   */
  onSubmit(event) {
    const form = event.target.closest('[js-blog-article="cartForm"]')

    if (!form) {
      return
    }

    if (typeof window.add_v_to_cart !== 'function' || !window.jQuery) {
      return
    }

    event.preventDefault()

    const variantId = form.querySelector('[name="id"]').value
    const button = form.querySelector('[js-blog-article="cartButton"]')

    window.add_v_to_cart(variantId, 1, false, window.jQuery(button))
  }
}

if (!customElements.get('upc-blog-article')) {
  customElements.define('upc-blog-article', UpcBlogArticle)
}
